import { useMemo, useState } from 'react';
import { Button, Chip, Input, toast } from '@heroui/react';
import { Segment } from '@heroui-pro/react/segment';
import { EmptyState } from '@heroui-pro/react/empty-state';
import { ArrowRight, Camera, ChevronDown, Clock3, FlaskConical, History, Pencil, Quote } from 'lucide-react';
import {
  DEFAULT_FILTER,
  weekNumber,
  withTandemFlag,
  type BoardAlert,
  type BoardFilter,
  type Me,
  type OfficeBoard,
  type RuleDef,
  type RuleId,
} from '@odd/domain';
import { useHistory, useSaveSettings, useTakeSnapshot } from '../lib/queries';
import { fmtShort, fmtSnapshot } from '../lib/format';
import { BoardContext } from '../components/BoardContext';
import { Pulse } from '../components/Pulse';
import { CaseTable } from '../components/Table';
import { Meetings } from '../components/Side';
import { TandemCards, TandemWorkspace, useTandemData } from '../components/Tandems';
import { Agreements } from '../components/Agreements';
import { CaseSheet } from '../components/CaseSheet';
import { Select } from '../components/ui';
import type { DashboardLayout } from '../lib/layout';

function IdeaFuerza({ board, officeId }: { board: OfficeBoard; officeId: string }) {
  const save = useSaveSettings(officeId);
  const [draft, setDraft] = useState<string | null>(null);
  const idea = board.settings.ideaFuerza;
  if (draft != null)
    return (
      <form
        className="flex flex-1 items-center gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate(
            { ideaFuerza: draft },
            { onSuccess: () => setDraft(null), onError: (er) => toast.danger(er.message) },
          );
        }}
      >
        <Input
          aria-label="Idea fuerza"
          autoFocus
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          className="flex-1"
        />
        <Button type="submit" size="sm" isPending={save.isPending}>
          Guardar
        </Button>
        <Button size="sm" variant="ghost" onPress={() => setDraft(null)}>
          Cancelar
        </Button>
      </form>
    );
  return (
    <button
      type="button"
      onClick={() => setDraft(idea ?? '')}
      className="board-idea-button group flex flex-1 items-center gap-3 text-left"
    >
      <Quote className="size-5 shrink-0 text-orange" />
      <span className={`text-lg font-extrabold ${idea ? 'text-ink' : 'text-ink-3'}`}>
        {idea ? `«${idea}»` : 'Añade la idea fuerza de estas semanas'}
      </span>
      {board.settings.ideaFuerzaSince && (
        <span className="hidden text-xs text-ink-3 sm:inline">
          desde el {fmtShort(board.settings.ideaFuerzaSince)}
        </span>
      )}
      <Pencil className="size-4 shrink-0 text-ink-3 opacity-0 transition-opacity group-hover:opacity-100" />
    </button>
  );
}

function WeekPicker({
  board,
  officeId,
  onSelectRun,
}: {
  board: OfficeBoard;
  officeId: string;
  onSelectRun: (id: string | null) => void;
}) {
  const history = useHistory(officeId);
  const list = history.data ?? [];
  if (list.length < 2 || !board.snapshot) return null;
  const latestId = list[0]!.runId;
  return (
    <Select
      label="Semana"
      className="board-week-picker w-56"
      value={board.snapshot.runId}
      onChange={(v) => onSelectRun(v === latestId ? null : v)}
      options={list.map((r, i) => ({
        value: r.runId,
        label: `Semana ${weekNumber(r.week)}${i === 0 ? ' · actual' : ''}${r.simulated ? ' · simulada' : ''}`,
      }))}
    />
  );
}

const tandemFromUrl = () => new URLSearchParams(window.location.search).get('tandem');
function setTandemInUrl(affId: string | null) {
  const url = new URL(window.location.href);
  if (affId) url.searchParams.set('tandem', affId);
  else url.searchParams.delete('tandem');
  window.history.replaceState(null, '', url);
}

/** Tándems a la izquierda (tarjetas seleccionables) y, a la derecha, la mesa de la oficina o el espacio del tándem */
function TandemsArea({ layout = 'classic' }: { layout?: DashboardLayout }) {
  const { tandems } = useTandemData();
  const [selected, setSelected] = useState<string | null>(tandemFromUrl);
  const [expanded, setExpanded] = useState(false);
  const tandem = tandems.find((t) => t.affId === selected) ?? null;
  const select = (affId: string | null) => {
    setSelected(affId);
    setTandemInUrl(affId);
    setExpanded(false);
  };
  return (
    <div
      id={layout === 'v2' ? 'mesa' : undefined}
      className="board-workspace grid items-start gap-6 lg:grid-cols-[320px_1fr]"
    >
      <div
        className="board-team-sidebar lg:sticky lg:top-20 lg:max-h-[calc(100vh-6rem)] lg:overflow-y-auto lg:pr-1"
        data-expanded={expanded}
      >
        {layout === 'v2' && (
          <>
            <div className="v2-team-heading">
              <span>Tu equipo</span>
              <span>{tandems.length} tándems</span>
            </div>
            <Button
              className="v2-team-toggle"
              variant="secondary"
              onPress={() => setExpanded((value) => !value)}
              aria-expanded={expanded}
              aria-controls="v2-team-list"
            >
              {tandem?.affName ?? 'Toda la oficina'} <ChevronDown size={15} aria-hidden="true" />
            </Button>
          </>
        )}
        <div
          id={layout === 'v2' ? 'v2-team-list' : undefined}
          className={layout === 'v2' ? 'v2-team-list' : undefined}
        >
          <TandemCards selected={tandem ? tandem.affId : null} onSelect={select} />
        </div>
      </div>
      <div className="min-w-0">
        {tandem ? <TandemWorkspace key={tandem.affId} tandem={tandem} layout={layout} /> : <CaseTable />}
      </div>
    </div>
  );
}

export function BoardView({
  board,
  me,
  officeId,
  onSelectRun,
  loading = false,
  layout = 'classic',
}: {
  board: OfficeBoard;
  me: Me;
  officeId: string;
  onSelectRun?: (id: string | null) => void;
  loading?: boolean;
  layout?: DashboardLayout;
}) {
  const v2 = layout === 'v2';
  const [filter, setFilter] = useState<BoardFilter>(DEFAULT_FILTER);
  const [open, setOpen] = useState<{ key: string; alert: BoardAlert; sequence?: string[] } | null>(null);
  const snap = useTakeSnapshot(officeId);

  const rules = useMemo(
    () => Object.fromEntries(board.rules.map((r) => [r.id, r])) as Record<RuleId, RuleDef>,
    [board.rules],
  );
  const alerts = useMemo(
    () => withTandemFlag(board.alerts, board.tandemSheets),
    [board.alerts, board.tandemSheets],
  );
  const openCase = (a: BoardAlert, opts?: { sequence?: string[] }) =>
    setOpen({ key: a.key, alert: a, sequence: opts?.sequence });
  const ctx = { board, me, officeId, rules, alerts, filter, openCase };
  // El caso abierto se refresca con el board (p. ej. tras guardar la hoja del tándem)
  const findCase = (key: string) =>
    alerts.find((a) => a.key === key) ?? alerts.find((a) => a.related?.some((r) => r.key === key));
  const openFresh = open ? (findCase(open.key) ?? open.alert) : null;
  const seqIndex = open?.sequence ? open.sequence.indexOf(open.key) : -1;
  const goTo = (key: string) => {
    const a = findCase(key);
    if (a && open) setOpen({ key, alert: a, sequence: open.sequence });
  };

  // Si el usuario no ve la oficina entera (su ámbito: él y sus subordinados), se dice cuánto ve
  const communitiesLabel =
    board.office.inScope != null
      ? `tu ámbito: ${board.office.inScope.toLocaleString('es-ES')} de ${board.office.communities.toLocaleString('es-ES')} comunidades`
      : `${board.office.communities.toLocaleString('es-ES')} comunidades`;
  const meta = [
    `Semana ${weekNumber(board.week)}`,
    board.snapshot ? `Foto del ${fmtSnapshot(board.snapshot.takenAt)}` : null,
    communitiesLabel,
    `${board.tandems.length} tándems`,
    board.office.directorNames[0] ? `DO: ${board.office.directorNames[0]}` : null,
    board.snapshot && !board.previousSnapshot ? 'sin foto de la semana anterior para comparar' : null,
  ].filter(Boolean);

  const controls = (
    <div className={`board-controls flex flex-wrap items-center gap-2 ${loading ? 'opacity-60' : ''}`}>
      {onSelectRun && <WeekPicker board={board} officeId={officeId} onSelectRun={onSelectRun} />}
      <Segment
        size="sm"
        aria-label="Segmento"
        selectedKey={filter.segment}
        onSelectionChange={(k) => setFilter((f) => ({ ...f, segment: k as BoardFilter['segment'] }))}
      >
        <Segment.Item id="AB">A + B</Segment.Item>
        <Segment.Item id="ALL">Todos</Segment.Item>
      </Segment>
    </div>
  );

  return (
    <BoardContext.Provider value={ctx}>
      <main
        id={v2 ? 'inicio' : undefined}
        className={`${v2 ? 'board-v2 ' : ''}mx-auto grid max-w-[1400px] gap-6 px-4 pt-6 pb-16 sm:px-6`}
      >
        {v2 ? (
          <section className="v2-board-context" aria-label="Contexto de la reunión">
            <div>
              <div className="v2-board-eyebrow">
                <span /> Operativa semanal <i />{' '}
                <span className="v2-board-week">Semana {weekNumber(board.week)}</span>
                {board.snapshot?.simulated && (
                  <Chip size="sm" variant="soft" color="warning">
                    <FlaskConical className="size-3" />
                    <Chip.Label>Datos simulados</Chip.Label>
                  </Chip>
                )}
              </div>
              <h1>{board.office.name}</h1>
              <p className="v2-board-meta">
                {communitiesLabel}
                <span>·</span>
                {board.tandems.length} tándems
                {board.office.directorNames[0] && (
                  <>
                    <span>·</span>DO: {board.office.directorNames.join(', ')}
                  </>
                )}
              </p>
            </div>
            <div className="v2-board-context-right">
              {controls}
              <p className="v2-snapshot-label">
                <Clock3 size={12} aria-hidden="true" />
                {board.snapshot
                  ? `Foto del ${fmtSnapshot(board.snapshot.takenAt)}`
                  : 'Pendiente de la primera foto'}
              </p>
              {board.snapshot && !board.previousSnapshot && (
                <p className="v2-no-comparison">Sin foto de la semana anterior para comparar</p>
              )}
            </div>
          </section>
        ) : (
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs font-bold tracking-[0.14em] text-orange-ink uppercase">
                  Reunión operativa semanal · 90 min
                </span>
                {board.snapshot?.simulated && (
                  <Chip size="sm" variant="soft" color="warning">
                    <FlaskConical className="size-3" />
                    <Chip.Label>Datos simulados</Chip.Label>
                  </Chip>
                )}
              </div>
              <h1 className="mt-1 text-3xl font-extrabold text-ink sm:text-4xl">{board.office.name}</h1>
              <div className="mt-1 text-sm text-ink-3">{meta.join(' · ')}</div>
            </div>
            {controls}
          </div>
        )}

        {!board.isCurrent && onSelectRun && (
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-warn bg-warn-soft px-5 py-3 text-sm text-warn-ink">
            <span className="flex items-center gap-2 font-semibold">
              <History className="size-4" /> Estás viendo la reunión de la semana {weekNumber(board.week)}.
              Los acuerdos y hojas que edites son de esa semana.
            </span>
            <Button size="sm" variant="secondary" onPress={() => onSelectRun(null)}>
              Volver a la semana actual
            </Button>
          </div>
        )}

        <div className="board-idea flex flex-wrap items-center gap-3 rounded-2xl border border-line bg-card px-5 py-4">
          {v2 && <span className="v2-idea-label">Idea fuerza</span>}
          <IdeaFuerza board={board} officeId={officeId} />
        </div>

        {!board.snapshot ? (
          <EmptyState className="rounded-2xl border border-dashed border-line py-16">
            <EmptyState.Header>
              <EmptyState.Media variant="icon">
                <Camera className="size-5" />
              </EmptyState.Media>
              <EmptyState.Title>Aún no hay foto para esta oficina</EmptyState.Title>
              <EmptyState.Description>La foto se genera cada día a las 18:00.</EmptyState.Description>
            </EmptyState.Header>
            {me.canAdmin && (
              <EmptyState.Content>
                <Button isPending={snap.isPending} onPress={() => snap.mutate()}>
                  Generar la foto ahora
                </Button>
              </EmptyState.Content>
            )}
          </EmptyState>
        ) : (
          <>
            <Pulse compact={v2} />
            {v2 && (
              <nav className="v2-meeting-nav" aria-label="Secciones de la reunión">
                <span>En esta reunión</span>
                <a href="#mesa">
                  La mesa <ArrowRight size={12} aria-hidden="true" />
                </a>
                <a href="#acuerdos">
                  Acuerdos <span>{board.currentAgreements.length}</span>
                </a>
                <a href="#juntas">Próximas juntas</a>
                <span className="v2-meeting-duration">
                  <Clock3 size={12} aria-hidden="true" /> 90 min
                </span>
              </nav>
            )}
            <TandemsArea layout={layout} />
            <div className="board-follow-up grid items-start gap-6 xl:grid-cols-[1fr_380px]">
              {v2 ? (
                <div className="v2-agreements">
                  <Agreements currentAnchor="acuerdos" />
                </div>
              ) : (
                <Agreements />
              )}
              {v2 ? (
                <div id="juntas" className="v2-meetings">
                  <Meetings />
                </div>
              ) : (
                <Meetings />
              )}
            </div>
            {v2 && (
              <footer className="v2-board-footer">
                <span>Eleva · Operativa semanal</span>
                <a href="#inicio">Volver al inicio ↑</a>
              </footer>
            )}
          </>
        )}
      </main>
      <CaseSheet
        layout={layout}
        alert={openFresh}
        onClose={() => setOpen(null)}
        fill={
          open?.sequence && seqIndex >= 0
            ? {
                index: seqIndex,
                total: open.sequence.length,
                prev: seqIndex > 0 ? () => goTo(open.sequence![seqIndex - 1]!) : undefined,
                next:
                  seqIndex < open.sequence.length - 1 ? () => goTo(open.sequence![seqIndex + 1]!) : undefined,
              }
            : undefined
        }
      />
    </BoardContext.Provider>
  );
}
